import PropTypes from 'prop-types';
import React from 'react';

import logger from '@/shared/utils/logger';

/**
 * Keeps a map's rendering failure from taking the chat page down with it.
 *
 * WHAT IT IS: an error boundary, React's mechanism for catching errors thrown
 * while rendering (or in a useEffect) anywhere inside it. Without one, React
 * unmounts the whole tree, so one malformed map would blank the entire chat.
 * With this around each map, React shows `fallback` in the map's place and
 * leaves the message text and every other message alone.
 *
 * WHY A CLASS: React only offers this through two class methods,
 * getDerivedStateFromError (switch to the fallback) and componentDidCatch
 * (report it). There is no hook equivalent. This is the client's first error
 * boundary, scoped deliberately to the map subtree (#1190).
 *
 * WHAT IT DOES NOT CATCH: errors in event handlers (e.g. a click listener),
 * timers, or async code. Those still need an ordinary try/catch.
 *
 * Generic on purpose: hub #1191 (re-hydrating saved maps) wraps its maps in
 * the same component with the same MapUnavailable fallback.
 */
export default class MapErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error, info) {
        logger.error(
            {
                ...this.props.context,
                error: error?.message ?? String(error),
                componentStack: info?.componentStack,
            },
            'Map failed to render; showing fallback'
        );
    }

    componentDidUpdate(prevProps) {
        // A different payload (e.g. the next message's map) gets a fresh try
        // instead of inheriting the previous one's failure.
        if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
            this.setState({ hasError: false });
        }
    }

    render() {
        return this.state.hasError ? this.props.fallback : this.props.children;
    }
}

MapErrorBoundary.propTypes = {
    /** The map subtree to protect */
    children: PropTypes.node,
    /** What to render instead after an error (e.g. <MapUnavailable/>) */
    fallback: PropTypes.node.isRequired,
    /** When this changes, a caught error is cleared and children re-render */
    resetKey: PropTypes.any,
    /** Extra fields for the log entry, e.g. { map_id } */
    context: PropTypes.object,
};

MapErrorBoundary.defaultProps = {
    context: {},
};
